import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchUserMicrosoftStatus } from "../../../api/microsoft";
import { userHasAdminRole, userShouldSeeEmailSignatureNag } from "./userSetupStatus";

/**
 * Who should see the email-signature nag (banner + profile checklist).
 * Admins are eligible immediately. Everyone else waits on GET /microsoft/me/status.
 */
export function useEmailSignatureNagAudience() {
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = userHasAdminRole(currentUser);
  const [microsoftStatus, setMicrosoftStatus] = useState(null);
  const [audienceReady, setAudienceReady] = useState(isAdmin);

  useEffect(() => {
    if (!currentUser?.id) {
      setMicrosoftStatus(null);
      setAudienceReady(false);
      return;
    }
    if (isAdmin) {
      setAudienceReady(true);
      return;
    }

    let cancelled = false;
    setAudienceReady(false);
    (async () => {
      try {
        const status = await fetchUserMicrosoftStatus();
        if (!cancelled) setMicrosoftStatus(status);
      } catch (error) {
        const httpStatus = error?.response?.status;
        if (!cancelled) {
          setMicrosoftStatus(
            httpStatus === 404 || httpStatus === 501
              ? { connected: false, can_send_mail: false, apiMissing: true }
              : { connected: false, can_send_mail: false }
          );
        }
      } finally {
        if (!cancelled) setAudienceReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, isAdmin]);

  return {
    isAdmin,
    microsoftStatus,
    audienceReady,
    eligible: userShouldSeeEmailSignatureNag({ isAdmin, microsoftStatus }),
  };
}
